const ENTITY_MEMBERSHIP_FIELDS = {
    workspace: [{field: "owner"}],
    userRole: [{field: "workspace_membership"}],
    auditLog: [{field: "actor"}],
    userEventLog: [{field: "actor"}],
    workspaceInvitation: [{field: "inviter"}],
};

function asArray(value) {
    if (value === null || value === undefined || value === "") return [];
    return Array.isArray(value) ? value.filter(Boolean) : [value];
}

function relationOperations(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        && ["connect", "set", "disconnect"].some((key) => value[key] !== undefined);
}

function keyCandidates(value) {
    if (value === null || value === undefined) return [];
    if (typeof value !== "object") return [String(value)];
    return [value.documentId, value.id]
        .filter((item) => item !== null && item !== undefined)
        .map(String);
}

function sameIdentity(left, right) {
    const rightKeys = new Set(keyCandidates(right));
    return keyCandidates(left).some((key) => rightKeys.has(key));
}

export function membershipMember(membership) {
    return membership?.member || null;
}

export function membershipIdentifier(membership) {
    return membership?.documentId || membership?.id || null;
}

export function membershipLabel(membership) {
    const member = membershipMember(membership);
    return member?.fullname
        || [member?.firstname, member?.lastname].filter(Boolean).join(" ")
        || member?.username
        || member?.email
        || "";
}

export function findMembershipForValue(memberships, value) {
    const list = Array.isArray(memberships) ? memberships : [];
    if (!value) return null;
    const matches = list.filter((membership) => sameIdentity(membership, value));
    if (matches.length === 1) return matches[0];
    return value?.member ? value : null;
}

function normalizeMembershipValue(value, many, memberships) {
    const current = asArray(value)
        .map((item) => findMembershipForValue(memberships, item) || item)
        .filter((item) => item?.member);
    return many ? current : current[0] || null;
}

export function normalizeMembershipEntity(entity, kind, memberships = []) {
    if (!entity || typeof entity !== "object") return entity;
    const normalized = {...entity};
    (ENTITY_MEMBERSHIP_FIELDS[kind] || []).forEach((relation) => {
        normalized[relation.field] = normalizeMembershipValue(
            entity[relation.field],
            Boolean(relation.many),
            memberships,
        );
    });
    return normalized;
}

function relationReference(membership) {
    const identifier = membershipIdentifier(membership);
    if (!identifier) {
        const error = new Error("workspace-membership.error.membership-not-found");
        error.code = "workspace-membership.error.membership-not-found";
        throw error;
    }
    return typeof identifier === "string" ? {documentId: identifier} : {id: identifier};
}

function serializeMembershipValue(value, memberships) {
    if (value === null || value === "") return null;
    if (value === undefined) return undefined;
    if (Array.isArray(value)) {
        return value.map((item) => {
            const membership = findMembershipForValue(memberships, item);
            if (!membership) throw new Error("workspace-membership.error.membership-not-found");
            return relationReference(membership);
        });
    }
    if (relationOperations(value)) {
        return Object.fromEntries(["connect", "set", "disconnect"]
            .filter((key) => value[key] !== undefined)
            .map((key) => [key, asArray(value[key]).map((item) => {
                const membership = findMembershipForValue(memberships, item);
                if (!membership) throw new Error("workspace-membership.error.membership-not-found");
                return relationReference(membership);
            })]));
    }
    const membership = findMembershipForValue(memberships, value);
    if (!membership) throw new Error("workspace-membership.error.membership-not-found");
    return membershipIdentifier(membership);
}

export function prepareMembershipPayload(kind, input, memberships = [], options = {}) {
    if (!input || typeof input !== "object") return input;
    const data = {...input};

    (ENTITY_MEMBERSHIP_FIELDS[kind] || []).forEach((relation) => {
        const provided = Object.prototype.hasOwnProperty.call(data, relation.field);
        if (!provided && relation.actorOnCreate && options.operation === "create" && options.currentMembership) {
            data[relation.field] = membershipIdentifier(options.currentMembership);
            return;
        }
        if (!provided) return;
        data[relation.field] = serializeMembershipValue(data[relation.field], memberships);
    });

    return data;
}

export function normalizeWorkspaceIdentityGraph(workspace, memberships = []) {
    if (!workspace || typeof workspace !== "object") return workspace;
    return normalizeMembershipEntity(workspace, "workspace", memberships);
}

export {ENTITY_MEMBERSHIP_FIELDS};
